import { createStore } from 'react-sweet-state';

import { mapBriefFromGenerationResult, mapToolContentForBriefInput } from 'src/data/brief';
import { isRecomposeGenerationResult } from 'src/data/generation';
import { isFreestyleGenerationTool, isSummarizerGenerationTool } from 'src/data/generationTool';
import { alignSummarizerSuggestionModeForGeneration } from 'src/data/utils';

const INITIAL_STATE = {
  resultData: null,
  isRecomposeResult: false,
  isExpandedHeader: false,
};

export const PRIVATE_ACTIONS = {
  initStore:
    () =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.updateResultData());
    },

  updateStore:
    () =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.updateResultData());
    },

  updateResultData:
    () =>
    ({ setState }, { result }) => {
      setState({
        resultData: result,
        isRecomposeResult: isRecomposeGenerationResult(result),
      });
    },

  updateVariationInGenerationResults:
    (variation) =>
    ({ getState, setState }) => {
      const { resultData } = getState();

      // Update variation in resultData list
      const newVariations = resultData.variations?.map((currentVariation) => {
        if (currentVariation.id === variation.id) {
          return {
            ...currentVariation,
            ...variation,
          };
        } else if (!currentVariation.benchmarkConfig) {
          return {
            ...currentVariation,
            benchmarkConfig: variation.benchmarkConfig,
          };
        }
        return currentVariation;
      });

      setState({ resultData: { ...resultData, variations: newVariations } });
    },
};

const ACTIONS = {
  getGenerationResult:
    () =>
    (_, { result }) => {
      return result;
    },

  getGenerationTool:
    () =>
    (_, { result }) => {
      return result?.generationTool;
    },

  getBriefContentForGeneration:
    () =>
    (_, { result }) => {
      const generationTool = result?.generationTool;
      const briefContent = mapBriefFromGenerationResult({ generationTool, request: result });
      return mapToolContentForBriefInput(generationTool, briefContent);
    },

  getLanguageDetails:
    () =>
    (_, { result }) => {
      return {
        language: result?.language,
        targetFormality: result?.languageTargetFormality,
      };
    },

  isRecomposeResult:
    () =>
    ({ getState }) => {
      return getState().isRecomposeResult;
    },

  getSuggestFreestyleToolDetails:
    () =>
    (_, { result }) => {
      if (!isFreestyleGenerationTool(result?.generationTool)) {
        return null;
      }

      return {
        id: result?.freestyleToolId,
        title: result?.freestyleToolTitle,
        allInstructions: result?.freestyleToolAllInstructions,
      };
    },

  getSuggestionMode:
    () =>
    (_, { result }) => {
      return result?.suggestionMode;
    },

  getSuggestionModeForGeneration:
    () =>
    (_, { result }) => {
      const { generationTool, assetType, suggestionMode } = result || {};
      if (isSummarizerGenerationTool(generationTool)) {
        return alignSummarizerSuggestionModeForGeneration({ assetType, suggestionMode });
      }
      return suggestionMode;
    },

  getSuggestPersona:
    () =>
    (_, { result }) => {
      return result?.customerPersona;
    },

  getSuggestGenerationGuidance:
    () =>
    (_, { result }) => {
      return result?.generationGuidance;
    },

  getRecomposeOriginalVariationId:
    () =>
    (_, { result }) => {
      return result?.originalVariationId;
    },

  getRecomposeTov:
    () =>
    (_, { result }) => {
      if (!result?.tovTitle) {
        return null;
      }
      return {
        id: result.tovId,
        title: result.tovTitle,
      };
    },

  getRecomposeOriginalText:
    () =>
    (_, { result }) => {
      const texts = result?.inputTexts || [];
      return texts.join('\n');
    },

  toggleHeader:
    () =>
    ({ getState, setState }) => {
      const { isExpandedHeader } = getState();
      setState({ isExpandedHeader: !isExpandedHeader });
    },

  isExpandedHeader:
    () =>
    ({ getState }) => {
      return getState().isExpandedHeader;
    },

  updateVariation:
    (variation) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.updateVariationInGenerationResults(variation));
    },
};

export const GenerationResultStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'document-view-generation-result',
});
